import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

export function districtLandmark(district) {
  if (district === 'brackenwake') return { id: 'ashen-crown', x: -38, z: -38, scale: 0.65 };
  if (['chapel', 'red-chapel', 'duskfall-crypt'].includes(district)) return { id: 'red-chapel', x: -40, z: -40, scale: 0.65 };
  if (district === 'ironpass') return { id: 'ironpass', x: -36, z: -40, scale: 0.75 };
  return null;
}

export function createWorldAssetLoader(world) {
  const root = new THREE.Group();
  root.name = 'blender-landmark-canary';
  world.root.add(root);
  const loader = new GLTFLoader();
  const cache = new Map();
  const ownBlockers = new Set();
  let revision = 0;
  let disposed = false;
  let status = 'idle';
  let manifest;
  const getManifest = () => manifest ||= fetch('/assets/world/manifest.json').then((response) => {
    if (!response.ok) throw new Error('World asset manifest unavailable');
    return response.json();
  });

  function clearBlockers() {
    for (let index = world.blockers.length - 1; index >= 0; index -= 1) {
      if (ownBlockers.has(world.blockers[index])) world.blockers.splice(index, 1);
    }
    ownBlockers.clear();
  }

  function release(model) {
    const materials = new Set();
    model.traverse((mesh) => {
      mesh.geometry?.dispose();
      for (const mat of [].concat(mesh.material || [])) materials.add(mat);
    });
    materials.forEach((mat) => mat.dispose());
  }

  async function setDistrict(district) {
    const token = ++revision;
    root.clear();
    clearBlockers();
    const placement = districtLandmark(district);
    if (!placement || disposed) { status = 'idle'; return; }
    status = 'loading';
    try {
      let entry = cache.get(placement.id);
      if (!entry) {
        const data = await getManifest();
        const spec = data.assets.find((asset) => asset.id === placement.id);
        if (!spec || !/^[a-z-]+\.glb$/.test(spec.file)) throw new Error('Unknown castle asset');
        const result = await loader.loadAsync(`/assets/world/${spec.file}`);
        if (disposed) { release(result.scene); return; }
        entry = { model: result.scene, spec };
        const previous = cache.get(placement.id);
        if (previous) { release(entry.model); entry = previous; }
        else cache.set(placement.id, entry);
      }
      if (disposed || token !== revision) return;
      const { model, spec } = entry;
      model.position.set(placement.x, world.getGroundHeight(placement.x, placement.z), placement.z);
      model.scale.setScalar(placement.scale);
      root.add(model);
      world.cutaway.register(model);
      for (const collider of spec.colliders) {
        const blocker = { x: placement.x + collider.x * placement.scale,
          z: placement.z + collider.z * placement.scale, radius: collider.radius * placement.scale };
        world.blockers.push(blocker);
        ownBlockers.add(blocker);
      }
      status = 'ready';
    } catch (error) {
      if (!disposed && token === revision) {
        status = 'failed';
        console.warn('Lonewolf castle canary:', error.message);
      }
    }
  }

  function dispose() {
    disposed = true;
    revision += 1;
    clearBlockers();
    root.clear();
    cache.forEach((entry) => release(entry.model));
    cache.clear();
    world.root.remove(root);
  }
  return { setDistrict, dispose, snapshot: () => ({ status, meshes: root.children.length, colliders: ownBlockers.size }) };
}
